import { SmartusComponentsModule } from '@affinis/smartus-components';
import { CardConfig } from '@affinis/smartus-components/lib/card/card.component.types';
import { Tab } from '@affinis/smartus-components/lib/tabs/tabs.components.types';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BaseSelectViewModel,
  BuildingRoomViewModel,
  CreateArchObjUtilHistoryRequest,
  DtUsageCategoryViewModel,
  UpdateRoomRequest,
} from '@core/api';
import { RoutePath } from '@core/routing/routing.types';
import { Billing } from '@core/services/edit-room/billing';
import { EditRoomService } from '@core/services/edit-room/edit-room.service';
import { UserService } from '@core/services/oauth-service/user.service';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OverrideModule } from '@override/override.module';
import { UserRoles } from '@shared/constants/user-roles.constants';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { BreadcrumbService } from 'src/app/core/services/breadcrumb-service/BreadcrumbService';
import { SafeHtmlPipe } from '../../../../app/shared/pipes/safe-html.pipe';
import { MaintenanceRoomAddNewModalComponent } from './maintenance-room/maintenance-room-add-new-modal/maintenance-room-add-new-modal.component';
import { MaintenanceRoomComponent } from './maintenance-room/maintenance-room.component';

@Component({
  selector: 'app-edit-room',
  imports: [
    CommonModule,
    FormlyModule,
    SmartusComponentsModule,
    TranslateModule,
    MaintenanceRoomComponent,
    OverrideModule,
    SafeHtmlPipe,
  ],
  templateUrl: './edit-room.component.html',
  styleUrl: './edit-room.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditRoomComponent implements OnInit, AfterViewInit {
  private translateService = inject(TranslateService);
  private breadcrumbsService = inject(BreadcrumbService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cd = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  public dialogRef: MatDialogRef<EditRoomComponent>;
  private dialog = inject(MatDialog);
  private userService = inject(UserService);
  private editRoomService = inject(EditRoomService);
  private toastr = inject(ToastrService);

  @Input() roomId: string;
  @Input() isEmbedded: boolean = false;
  @Output() closeEmbeddedView = new EventEmitter<void>();
  @Output() componentLoaded = new EventEmitter<void>();
  @Output() navigateToWorkspace = new EventEmitter<string>();

  formModel: {
    primaryRoomNumber: string;
    aoid: string;
    magOffice: string;
    wmProd: string;
    designType: string;
    warmRentCluster: string;
    usersAreaArchitectural: string;
    rentalArea_REM: string;
    userArea: string;
    workingPlacesOrPlanning: string;
    workingPlacesOrPlanningAvailable: string;
    workingPlacesOrPlanningOccupied: string;
    workingPlacesOrPlanningPlanned: string;
    roomDescription: string;
    warmClusterIndividual: string;
    wmClusterInfo: string;
    warmRentCalculation: boolean;
    hasWmvContract: boolean;
    isRoutineCleaning: boolean;
    magOfficeDetailAreaType: string;
    billingArea: string;
    wmProdIndi: string;
    calculationType: string;
    tiFlag: boolean;
    centralInformation: string;
    typeOfUseOrDetailedArea: string;
    changeReason: string;
    wmPrice: number;
  };
  form: FormGroup;
  formFields: FormlyFieldConfig[];

  cardFields: CardConfig = {
    footer: {
      button2: {
        label: this.translateService.instant('edit_room.labels.cancel'),
        onClick: () => {
          if (window.opener || window.history.length === 1) {
            window.close();
          } else {
            this.router.navigateByUrl(`/${RoutePath.DATA_MAINTENANCE}`);
          }
        },
        disabled: false,
        type: 'button',
      },
      button1: {
        label: this.translateService.instant('edit_room.labels.save'),
        onClick: () => {
          this.onSubmit();
        },
        disabled: true,
        type: 'submit',
      },
    },
  };

  tabs: Tab[] = [
    {
      label: this.translateService.instant('edit_room.labels.roomDateTab'),
      id: 'roomData',
      onClick: (id: string) => this.onTabClick(id),
    },
    {
      label: this.translateService.instant('edit_room.labels.userAreaTab'),
      id: 'typeOfUseOrDetailedArea',
      onClick: (id: string) => this.onTabClick(id),
    },
  ];

  activeTab = this.tabs[0].id;
  isLoading: boolean = false;
  roomTitle: string;
  typeOfUseDetailedId: DtUsageCategoryViewModel[];
  typeOfUseDetailAreaData$: BehaviorSubject<BuildingRoomViewModel> =
    new BehaviorSubject<BuildingRoomViewModel>(null);
  spaceId: string;
  formattedSpaceId: string;
  changeReasons: BaseSelectViewModel[] = [];
  wmvClusters: BaseSelectViewModel[] = [];
  wmvClusterInfos: BaseSelectViewModel[] = [];
  wmvProducts: BaseSelectViewModel[] = [];
  calculationTypes: BaseSelectViewModel[] = [];
  calculationTypesInitial: BaseSelectViewModel[] = [];
  billingAreas: BaseSelectViewModel[] = [];
  centralInfos: BaseSelectViewModel[] = [];
  officeAreaTypes: BaseSelectViewModel[] = [];
  initialFormState: any;
  initialValues: Partial<UpdateRoomRequest> = {};
  initialValueChangeReason: string;
  initialBillingArea: string;
  frozenZoneStartDate = '';
  frozenZoneEndDate = '';
  frozenStatus: boolean = false;
  description = '';

  descriptionStyle =
    'background-color: #E20074; color: white; border-radius: 5px; padding: 15px;';
  descriptionContainerStyle =
    'width: 100%; max-width: 840px; border-radius: 15px; padding: 0 15px;';

  room: BuildingRoomViewModel;

  ngOnInit(): void {
    const roomDataSource = this.isEmbedded
      ? this.loadRoomDataForEmbedded()
      : this.loadRoomDataFromRoute();

    roomDataSource
      .pipe(
        switchMap(roomData => {
          if (roomData) {
            this.room = roomData.room || roomData;
            this.roomId = roomData.roomId || this.roomId;
            this.initializeRoomFormData(this.room);
          }

          return forkJoin({
            frozenZoneStatus: this.editRoomService
              .checkFrozenZone()
              .pipe(catchError(() => of({ startDate: '', endDate: '' }))),
            changeReasons: this.editRoomService.getChangeReasons(),
          });
        }),
        finalize(() => {
          this.isLoading = false;
          if (this.isEmbedded) {
            setTimeout(() => this.componentLoaded.emit(), 100);
          }
        })
      )
      .subscribe({
        next: () => {
          this.typeOfUseDetailAreaData$.next(this.room);
          this.spaceId = this.room.spaceId;

          if (!this.isEmbedded) {
            this.breadcrumbsService.setBreadcrumbs([{ label: this.spaceId }]);
          }

          this.formattedSpaceId =
            this.translateService.instant('edit_room.labels.room') +
            ': ' +
            this.spaceId.split('/').pop() +
            (this.room.roomDescription
              ? ' / ' + this.room.roomDescription
              : '');
          this.initialValueChangeReason = this.room.changeReason;
          this.initialBillingArea = this.room.billingAreaId;
          this.retrieveInitialData();
          this.formModel = this.initializeFormModel(this.room);
          this.typeOfUseDetailedId = this.room.usageCategories;
          this.initializeForm();

          this.form.valueChanges.subscribe(() => {
            this.cardFields.footer.button1.disabled = !this.form.valid;
          });

          this.cd.markForCheck();
        },
        error: error => {
          console.error('Error loading room data:', error);
          this.isLoading = false;
          if (this.isEmbedded) {
            this.componentLoaded.emit();
          }
        },
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.cd.detectChanges();
    }, 0);
  }

  private initializeRoomFormData(room: any): void {
    if (!room) return;

    this.formModel = room;
    this.initializeForm();

    if (this.frozenStatus) {
      this.disableFormFields();
    }
  }

  private loadRoomDataForEmbedded(): Observable<any> {
    if (!this.roomId) return of(null);
    this.isLoading = true;
    return this.editRoomService.getBuildingMaintenanceRoomInformation(
      this.roomId
    );
  }

  private loadRoomDataFromRoute(): Observable<any> {
    return this.route.data.pipe(map(data => data.roomData));
  }

  initializeFormModel(roomData: BuildingRoomViewModel) {
    return {
      primaryRoomNumber: roomData.primaryRoomNumber || '--',
      aoid: roomData.aoid || '--',
      magOffice: roomData?.magOfficeString || '--',
      wmProd: roomData?.wmProd || '--',
      designType: roomData?.measurement || '--',
      warmRentCluster: roomData?.wmCluster || '--',
      typeOfUseOrDetailedArea: roomData.usageTypeDetailAreaType || '--',
      usersAreaArchitectural: this.formatArea(roomData?.architecturalArea),
      rentalArea_REM: this.formatArea(roomData?.rentalArea_REM),
      userArea: this.formatArea(roomData?.userArea),
      workingPlacesOrPlanning:
        roomData?.cntWorkspacesPlanned !== undefined
          ? roomData?.cntWorkspacesPlanned.toString()
          : '--',
      workingPlacesOrPlanningAvailable:
        roomData?.cntWorkspacesAvailable !== undefined
          ? roomData?.cntWorkspacesAvailable.toString()
          : '--',
      workingPlacesOrPlanningOccupied:
        roomData?.cntWorkspacesOccupied !== undefined
          ? roomData?.cntWorkspacesOccupied.toString()
          : '--',
      workingPlacesOrPlanningPlanned:
        roomData?.cntWorkspacesPlanned !== undefined
          ? roomData?.cntWorkspacesPlanned.toString()
          : '--',
      warmRentCalculation: roomData.isWmv ?? false,
      hasWmvContract: roomData.isWarmRentalContractValid ?? false,
      isRoutineCleaning: roomData.isRoutineCleaning ?? false,
      tiFlag: roomData.tiFlag ?? false,
      roomDescription: roomData?.roomDescription,
      wmProdIndi: roomData?.wmProdIndi ?? null,
      warmClusterIndividual: roomData?.wmClusterIndi ?? null,
      wmClusterInfo: roomData?.wmClusterInfo ?? null,
      magOfficeDetailAreaType: roomData?.magOfficeDetailAreaType ?? null,
      calculationType: roomData?.calculationType,
      billingArea: roomData?.billingAreaId,
      centralInformation: roomData?.centralInfo ?? null,
      changeReason: '',
      wmPrice: roomData?.wmPrice ?? null,
    };
  }

  initializeForm(): void {
    this.form = this.fb.group({
      primaryRoomNumber: [this.formModel.primaryRoomNumber || '--'],
      aoid: [this.formModel.aoid || '--'],
      magOffice: [this.formModel.magOffice || '--'],
      wmProd: [this.formModel.wmProd || '--'],
      designType: [this.formModel.designType || '--'],
      warmRentCluster: [this.formModel.warmRentCluster || '--'],
      typeOfUseOrDetailedArea: [this.formModel.typeOfUseOrDetailedArea || '--'],
      rentalArea_REM: [this.formModel.rentalArea_REM || '--'],
      usersAreaArchitectural: [this.formModel.usersAreaArchitectural || '--'],
      userArea: [this.formModel.userArea || '--'],
      workingPlacesOrPlanning: [this.formModel.workingPlacesOrPlanning || '--'],
      workingPlacesOrPlanningAvailable: [
        this.formModel.workingPlacesOrPlanningAvailable || '--',
      ],
      workingPlacesOrPlanningOccupied: [
        this.formModel.workingPlacesOrPlanningOccupied || '--',
      ],
      workingPlacesOrPlanningPlanned: [
        this.formModel.workingPlacesOrPlanningPlanned || '--',
      ],
      warmRentCalculation: [this.formModel.warmRentCalculation ?? false],
      isRoutineCleaning: [this.formModel.isRoutineCleaning ?? false],
      tiFlag: [this.formModel.tiFlag ?? false],
      roomDescription: [this.formModel.roomDescription || '--'],
      wmProdIndi: [this.formModel.wmProdIndi || null],
      warmClusterIndividual: [this.formModel.warmClusterIndividual || null],
      wmClusterInfo: [this.formModel.wmClusterInfo || null],
      magOfficeDetailAreaType: [this.formModel.magOfficeDetailAreaType || null],
      calculationType: [this.formModel.calculationType || ''],
      billingArea: [{ value: this.formModel.billingArea, disabled: true }],
      centralInformation: [this.formModel.centralInformation || null],
      changeReason: [this.formModel.changeReason || ''],
      wmPrice: [this.formModel.wmPrice || null],
    });

    this.formFields = this.getFormFields();
  }

  private retrieveInitialData(): void {
    forkJoin({
      frozenZoneStatus: this.editRoomService
        .checkFrozenZone()
        .pipe(catchError(() => of({ startDate: '', endDate: '' }))),
      changeReasons: this.editRoomService
        .getChangeReasons()
        .pipe(catchError(() => of([]))),
      wmvClusters: this.editRoomService
        .getWmvClusters()
        .pipe(catchError(() => of([]))),
      wmvClusterInfos: this.editRoomService
        .getWmVClusterInfos()
        .pipe(catchError(() => of([]))),
      wmvProducts: this.editRoomService
        .getWmvProducts()
        .pipe(catchError(() => of([]))),
      calculationTypes: this.editRoomService
        .getCalculationTypes()
        .pipe(catchError(() => of([]))),
      billingAreas: this.editRoomService
        .getBillingAreas(this.spaceId)
        .pipe(catchError(() => of([]))),
      centralInfos: this.editRoomService
        .getCentralInfos()
        .pipe(catchError(() => of([]))),
      officeAreaTypes: this.editRoomService
        .getOfficeAreaTypes()
        .pipe(catchError(() => of([]))),
    }).subscribe(results => {
      this.changeReasons = results.changeReasons;
      this.wmvClusters = results.wmvClusters;
      this.wmvClusterInfos = results.wmvClusterInfos;
      this.wmvProducts = results.wmvProducts;
      this.calculationTypes = results.calculationTypes;
      this.calculationTypesInitial = this.calculationTypes;
      this.billingAreas = results.billingAreas;
      this.centralInfos = results.centralInfos;
      this.officeAreaTypes = results.officeAreaTypes;

      const frozenZoneData = results.frozenZoneStatus;
      if (
        frozenZoneData?.startDate.length > 0 &&
        frozenZoneData?.endDate.length > 0
      ) {
        this.frozenZoneStartDate = frozenZoneData.startDate;
        this.frozenZoneEndDate = frozenZoneData.endDate;
        this.frozenStatus = true;
        this.description = this.translateService.instant(
          'data_maintenance.frozenZone',
          {
            frozenZoneStartDate: this.frozenZoneStartDate,
            frozenZoneEndDate: this.frozenZoneEndDate,
          }
        );
      } else {
        this.formFields = this.getFormFields();
        this.frozenStatus = false;
      }

      if (this.form && this.frozenStatus) {
        this.disableFormFields();
      }
      this.cd.detectChanges();
    });
  }

  disableFormFields() {
    if (this.frozenStatus && this.form) {
      this.form.disable();
      this.cd.markForCheck();
    }
  }

  onTabClick(value: string) {
    this.isLoading = true;
    this.activeTab = value;
    this.cd.detectChanges();
    setTimeout(() => {
      this.isLoading = false;
      this.cd.detectChanges();
    }, 500);
  }

  getFormFields(): FormlyFieldConfig[] {
    const loggedUserRoles = this.userService.getCurrentUserRoles();
    const isEditable = loggedUserRoles.includes(
      UserRoles.SMARTPORTAL_REM_DATENPFLEGE
    );
    const isEditableBothRoles =
      loggedUserRoles.includes(UserRoles.SMARTPORTAL_REM_FLM_DATENPFLEGE) ||
      loggedUserRoles.includes(UserRoles.SMARTPORTAL_REM_DATENPFLEGE);

    return [
      {
        key: 'primaryRoomNumber',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant(
            'edit_room.labels.primaryRoomNumber'
          ),
        },
      },
      {
        key: 'aoid',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant('edit_room.labels.aoid'),
        },
      },
      {
        key: 'magOffice',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant('edit_room.labels.magOffice'),
        },
      },
      {
        key: 'wmProd',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant('edit_room.labels.wmProd'),
        },
      },
      {
        key: 'designType',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant('edit_room.labels.designType'),
        },
      },
      {
        key: 'warmRentCluster',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant(
            'edit_room.labels.warmRentCluster'
          ),
        },
      },
      {
        key: 'wmPrice',
        type: 'input',
        wrappers: ['readOnly'],
        props: {
          label: this.translateService.instant('edit_room.labels.price'),
          addonRight: {
            text: '€',
            showAlways: false,
          },
        },
      },
      {
        key: 'typeOfUseOrDetailedArea',
        type: 'link-edit-tab',
        props: {
          label: this.translateService.instant(
            'edit_room.labels.typeOfUseOrDetailedArea'
          ),
          disabled: !isEditableBothRoles,
          value: this.formModel.typeOfUseOrDetailedArea,
          onClick: () => this.openAddModal(this.typeOfUseDetailedId),
        },
      },
      {
        key: 'usersArea',
        type: 'user-area-inputs',
        props: {
          disabled: !isEditable,
          label: this.translateService.instant('edit_room.labels.usersAreas'),
          architecture: this.formModel.usersAreaArchitectural,
          rentalArea_REM: this.formModel.rentalArea_REM,
          userArea: this.formModel.userArea,
        },
      },
      {
        key: 'workingPlacesOrPlanning',
        type: 'workingplaces-planning-input',
        props: {
          disabled: !isEditableBothRoles,
          label: this.translateService.instant(
            'edit_room.labels.workingPlacesOrPlanning'
          ),
          number: this.formModel.workingPlacesOrPlanningAvailable,
          planned: this.formModel.workingPlacesOrPlanningPlanned,
          occupied: this.formModel.workingPlacesOrPlanningOccupied,
        },
      },
      {
        type: 'flex-layout-col',
        fieldGroup: [
          {
            key: 'warmRentCalculation',
            type: 'checkbox',
            wrappers: ['no-border'],
            className: 'checkBox-margin-top',
            templateOptions: {
              disabled: !isEditable || this.formModel.hasWmvContract,
              label: this.formModel.hasWmvContract
                ? this.translateService.instant(
                    'edit_room.labels.rentalContractExistYes'
                  )
                : this.translateService.instant(
                    'edit_room.labels.rentalContractExistNo'
                  ),
              value: this.formModel.warmRentCalculation,
            },
          },
        ],
      },
      {
        type: 'flex-layout-col',
        fieldGroup: [
          {
            key: 'isRoutineCleaning',
            type: 'checkbox',
            wrappers: ['no-border'],
            className: 'margin-to-top me-3',
            props: {
              disabled:
                !isEditableBothRoles || !this.formModel.warmRentCalculation,
              label: this.translateService.instant(
                'edit_room.labels.isRoutineCleaning'
              ),
              value: this.formModel.isRoutineCleaning,
            },
          },
          {
            key: 'tiFlag',
            type: 'checkbox',
            wrappers: ['no-border'],
            className: 'margin-to-top',
            props: {
              disabled: !isEditableBothRoles,
              label: this.translateService.instant('edit_room.labels.tiFlag'),
              value: this.formModel.tiFlag ?? false,
            },
          },
        ],
      },
      {
        key: 'roomDescription',
        type: 'input',
        props: {
          disabled: !isEditableBothRoles,
          label: this.translateService.instant(
            'edit_room.labels.roomDescription'
          ),
        },
      },
      {
        key: 'wmProdIndi',
        type: 'select-with-buttons',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant('edit_room.labels.wmProdIndi'),
          disabled: !isEditable,
          options: this.wmvProducts.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          isDeletable: true,
          onlyDeletion: true,
        },
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            field.formControl?.valueChanges.subscribe(value => {
              if (value === 'Verwaltung') {
                this.calculationTypes = this.calculationTypes.filter(
                  c => c.id !== Billing.roomCostCenter
                );
              } else {
                this.calculationTypes = this.calculationTypesInitial;
              }
              const fieldToUpdate = this.formFields.find(
                f => f.key === 'calculationType'
              );
              if (fieldToUpdate) {
                fieldToUpdate.props.options = this.calculationTypes.map(
                  (type: BaseSelectViewModel) => ({
                    value: type.id,
                    label: type.name,
                  })
                );
              }
            });
          },
        },
      },
      {
        key: 'warmClusterIndividual',
        type: 'select-with-buttons',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant(
            'edit_room.labels.wmProdClusterIndividual'
          ),
          disabled: !isEditable,
          options: this.wmvClusters.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          isDeletable: true,
          onlyDeletion: true,
        },
      },
      {
        key: 'wmClusterInfo',
        type: 'select-with-buttons',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant(
            'edit_room.labels.wmClusterInfo'
          ),
          disabled: !isEditable,
          options: this.wmvClusterInfos.map((reason: BaseSelectViewModel) => ({
            value: reason.id,
            label: reason.name,
          })),
          isDeletable: true,
          onlyDeletion: true,
        },
      },
      {
        key: 'magOfficeDetailAreaType',
        type: 'select-with-buttons',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant(
            'edit_room.labels.magOfficeAreaType'
          ),
          disabled: !isEditableBothRoles,
          options: this.officeAreaTypes.map(
            (officeAreaTypes: BaseSelectViewModel) => ({
              value: officeAreaTypes.id,
              label: officeAreaTypes.name,
            })
          ),
          isDeletable: true,
          onlyDeletion: true,
        },
      },
      {
        key: 'calculationType',
        type: 'select',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant('edit_room.labels.settlementOn'),
          disabled: !isEditableBothRoles,
          options: this.calculationTypes.map((type: BaseSelectViewModel) => ({
            value: type.id,
            label: type.name,
          })),
        },
        hooks: {
          onInit: (field: FormlyFieldConfig) => {
            if (
              field.formControl?.value === Billing.magentaOffice &&
              field.formControl.enabled
            ) {
              field.formControl.disable();
            }
            if (field.formControl?.value !== Billing.billingArea) {
              this.form.get('billingArea')?.disable();
            }
            field.formControl?.valueChanges.subscribe(() => {
              if (Billing.billingArea !== field.formControl?.value) {
                this.form.get('billingArea')?.disable();
                this.form.get('billingArea')?.setValue('');
              } else {
                this.form.get('billingArea')?.enable();
                this.form.get('billingArea')?.setValue(this.initialBillingArea);
              }
            });

            const updateOptions = () => {
              const wmProd = this.formFields.find(f => f.key === 'wmProd')
                .formControl.value;

              field.props.options = this.calculationTypes.map(
                (type: BaseSelectViewModel) => ({
                  value: type.id,
                  label: type.name,
                  disabled: this.isCalculationTypeDisabled(type.id, wmProd),
                })
              );
            };
            updateOptions();
          },
        },
      },
      {
        key: 'billingArea',
        type: 'select',
        className: 'margin-to-top',
        defaultValue: this.initialBillingArea,
        props: {
          label: this.translateService.instant('edit_room.labels.billingArea'),
          options: this.billingAreas.map((type: BaseSelectViewModel) => ({
            value: type.id,
            label: type.name,
          })),
        },
      },
      {
        key: 'centralInformation',
        type: 'select-with-buttons',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant(
            'edit_room.labels.centralInformation'
          ),
          disabled: !isEditableBothRoles,
          options: this.centralInfos.map(
            (centralInfo: BaseSelectViewModel) => ({
              value: centralInfo.id,
              label: centralInfo.name,
            })
          ),
          isDeletable: true,
          onlyDeletion: true,
        },
      },
      {
        key: 'changeReason',
        type: 'input-search',
        className: 'margin-to-top',
        props: {
          label: this.translateService.instant(
            'buildingMaintenance.table.changeReason'
          ),
          disabled: !isEditableBothRoles,
          options: this.changeReasons.map(
            (changeReason: BaseSelectViewModel) => ({
              value: changeReason.id,
              label: changeReason.name,
            })
          ),
          required: true,
        },
      },
    ];
  }

  openAddModal(items: DtUsageCategoryViewModel[]): void {
    const dialogRef = this.dialog.open(MaintenanceRoomAddNewModalComponent, {
      minWidth: '40vw',
      minHeight: '35vh',
      data: {
        item: items,
        changeReasons: this.changeReasons,
        spaceId: this.spaceId,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((result: CreateArchObjUtilHistoryRequest) => {
        if (result) {
          this.refreshRoomData();
        }
      });
  }

  refreshRoomData(): void {
    if (this.frozenStatus) {
      return;
    }
    this.editRoomService
      .getBuildingMaintenanceRoomInformation(this.spaceId)
      .subscribe({
        next: (data: BuildingRoomViewModel) => {
          if (data) {
            this.updateBuildingData(data);
            this.typeOfUseDetailAreaData$.next(data);

            this.initialBillingArea = data.billingAreaId;
          }
        },
      });
  }

  updateBuildingData(building: BuildingRoomViewModel): void {
    if (this.typeOfUseDetailAreaData$) {
      this.typeOfUseDetailAreaData$.next(building);
    }
    this.formModel = this.initializeFormModel(building);

    this.formFields = this.getFormFields();

    this.cd.detectChanges();
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }

  onSubmit() {
    this.isLoading = true;

    const isBilling = Billing.billingArea === this.form.value.calculationType;

    const billingAreaValue = isBilling ? this.form.value.billingArea : '';

    const requestBody: Partial<UpdateRoomRequest> = {
      id: this.spaceId,
      name2: this.form.value.roomDescription,
      userArea: this.toBackendDecimal(this.form.value.usersArea),
      workspacesPlanned: this.form.value.workingPlacesOrPlanning,
      wmIndividualProduct: this.form.value.wmProdIndi,
      wmIndividualCluster: this.form.value.warmClusterIndividual,
      wmClusterInformation: this.form.value.wmClusterInfo,
      isWMVerrechnung: this.form.value.warmRentCalculation,
      areaType: this.form.value.magOfficeDetailAreaType,
      routineCleaning: this.form.value.isRoutineCleaning,
      calculationType: this.form.value.calculationType,
      billingArea: billingAreaValue,
      tiFlag: this.form.value.tiFlag,
      centralInfo: this.form.value.centralInformation,
      changeReason: this.form.value.changeReason,
    };

    this.initialBillingArea = billingAreaValue;

    if (Object.keys(requestBody).length > 0) {
      this.editRoomService
        .updateRoom(requestBody as UpdateRoomRequest)
        .subscribe({
          next: () => {
            this.toastr.success(
              this.translateService.instant('edit_room.toastr.success')
            );
            this.cardFields.footer.button1.disabled = true;
            this.refreshRoomData();
            this.isLoading = false;
            this.cd.markForCheck();
          },
          error: err => {
            this.isLoading = false;
            let errMessage = this.translateService.instant(
              'edit_room.toastr.error'
            );
            if (err.errorCode === 12700) {
              errMessage = this.translateService.instant(
                'edit_room.toastr.error_calculationType_not_editable'
              );
            }
            this.cd.detectChanges();
            this.toastr.error(errMessage);
          },
        });
    }
  }

  navigateToWorkspaceEdit(): void {
    if (this.isEmbedded) {
      this.navigateToWorkspace.emit(`${this.spaceId}/A001`);
    } else {
      this.router.navigateByUrl(
        `/${RoutePath.WORKING_SPACE}/${this.spaceId}/A001`
      );
    }
  }

  private formatArea(value: number | undefined): string {
    return value !== undefined ? value.toString().replace('.', ',') : '--';
  }

  private toBackendDecimal(value: any): any {
    return typeof value === 'string' ? value.replace(',', '.') : value;
  }

  isCalculationTypeDisabled(billing: string, wmProd: string): boolean {
    if (billing === Billing.magentaOffice || billing === Billing.access) {
      return true;
    }
    return (
      (wmProd === 'Verwaltung' && billing === Billing.roomCostCenter) ||
      (wmProd === 'Lager' && billing === Billing.billingArea) ||
      (wmProd !== 'Verwaltung' && billing === Billing.billingArea)
    );
  }

  get adaptedCardFields(): CardConfig {
    if (this.isEmbedded) {
      return {
        ...this.cardFields,
        footer: {
          ...this.cardFields.footer,
          button2: {
            ...this.cardFields.footer.button2,
            onClick: () => {
              this.closeEmbeddedView.emit();
            },
          },
        },
      };
    }
    return this.cardFields;
  }

  hasRequiredRole(): Observable<boolean> {
    return this.userService.checkIfUserHasRole(
      UserRoles.SMARTPORTAL_OCCUPANCY_CREATE
    );
  }
}
